import React, { useEffect, useState, useRef } from 'react';
import { safeArray, unwrapListResponse } from '../../components/ErrorBoundary';
import { useProjects } from '../../context/ProjectsContext';
import ProjectFilterSelect from '../../components/ProjectFilterSelect';
import ResetFiltersButton from '../../components/ResetFiltersButton';
import { printTableElement } from '../../utils/exportUtils';
import { scopeAPI } from '../../services/authService';
import { confirmDialog } from '../../utils/confirmDialog';
import { notifySuccess, notifyError } from '../../utils/notify';
import { formatDateIN } from '../../utils/dateFormat';

const validatePhone = (v) => /^\d{10}$/.test(String(v || '').replace(/\s/g, ''));

const SiteOffice = () => {
  const { projects: projectOptions } = useProjects();
  const [dataReady, setDataReady] = useState(false);
  const [projectFilter, setProjectFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editOffice, setEditOffice] = useState(null);
  const [siteOffices, setSiteOffices] = useState([]);
  const [formError, setFormError] = useState('');
  const [newOffice, setNewOffice] = useState({
    project_id: '',
    office_name: '',
    address: '',
    contact_person: '',
    contact_phone: '',
    contact_email: '',
    setup_date: '',
    facilities: '',
    status: 'Active',
  });
  const contentRef = useRef(null);

  useEffect(() => {
    const loadSiteOffices = async () => {
      try {
        const response = await scopeAPI.getSiteOffices();
        const { rows } = unwrapListResponse(response);
        const mapped = safeArray(rows).map((row) => ({
          id: row.id,
          project_id: row.projectId ?? row.project_id ?? 1,
          office_name: row.officeName ?? row.office_name ?? '',
          address: row.address || '',
          contact_person: row.contactPerson ?? row.contact_person ?? '',
          contact_phone: row.contactPhone ?? row.contact_phone ?? '',
          contact_email: row.contactEmail ?? row.contact_email ?? '',
          area_sqft: row.areaSqft ?? row.area_sqft ?? 0,
          setup_date: row.setupDate ? String(row.setupDate).slice(0, 10) : (row.setup_date || ''),
          facilities: row.facilities || '',
          capacity: row.capacity ?? 0,
          monthly_rent: row.monthlyRent ?? row.monthly_rent ?? 0,
          status: row.status || 'Active',
        }));
        setSiteOffices(mapped);
      } catch (error) {
        console.warn('Site office API unavailable, using local data:', error?.message || error);
        setSiteOffices([]);
      } finally {
        setDataReady(true);
      }
    };
    loadSiteOffices();
  }, []);

  const projects = safeArray(projectOptions);

  if (!dataReady) {
    return (
      <div>
        <div className="section-header"><h2>Site Office</h2></div>
        <p style={{ marginTop: 8, fontSize: 12, color: '#6b7280' }}>Loading...</p>
      </div>
    );
  }

  const openAddModal = () => {
    setEditOffice(null);
    setFormError('');
    setNewOffice({
      project_id: '',
      office_name: '',
      address: '',
      contact_person: '',
      contact_phone: '',
      contact_email: '',
      setup_date: '',
      facilities: '',
      status: 'Active',
    });
    setShowModal(true);
  };

  const openEditModal = (office) => {
    setEditOffice(office);
    setFormError('');
    setNewOffice({
      project_id: String(office.project_id),
      office_name: office.office_name || '',
      address: office.address || '',
      contact_person: office.contact_person || '',
      contact_phone: office.contact_phone || '',
      contact_email: office.contact_email || '',
      setup_date: office.setup_date || '',
      facilities: office.facilities || '',
      status: office.status || 'Active',
    });
    setShowModal(true);
  };

  const handleCreateSiteOffice = async () => {
    setFormError('');
    if (!newOffice.project_id || !newOffice.office_name.trim() || !newOffice.address.trim()) {
      setFormError('Project, office name, and address are required.');
      return;
    }
    if (newOffice.contact_phone && !validatePhone(newOffice.contact_phone)) {
      setFormError('Phone must be 10 digits.');
      return;
    }
    if (!(await confirmDialog('Add this site office?', 'New Site Office'))) return;
    try {
      const payload = {
        projectId: Number(newOffice.project_id),
        officeName: newOffice.office_name.trim(),
        address: newOffice.address.trim(),
        contactPerson: newOffice.contact_person.trim() || null,
        contactPhone: newOffice.contact_phone.trim() || null,
        contactEmail: newOffice.contact_email.trim() || null,
        setupDate: newOffice.setup_date ? new Date(newOffice.setup_date).toISOString() : null,
        facilities: newOffice.facilities.trim() || null,
        status: newOffice.status,
      };
      const response = await scopeAPI.createSiteOffice(payload);
      const row = response?.data;
      setSiteOffices((prev) => ([
        {
          id: row.id,
          project_id: row.projectId ?? Number(newOffice.project_id),
          office_name: row.officeName ?? newOffice.office_name,
          address: row.address ?? newOffice.address,
          contact_person: row.contactPerson ?? newOffice.contact_person,
          contact_phone: row.contactPhone ?? newOffice.contact_phone,
          contact_email: row.contactEmail ?? newOffice.contact_email,
          area_sqft: row.areaSqft ?? 0,
          setup_date: row.setupDate ? String(row.setupDate).slice(0, 10) : (newOffice.setup_date || ''),
          facilities: row.facilities ?? newOffice.facilities,
          capacity: row.capacity ?? 0,
          monthly_rent: row.monthlyRent ?? 0,
          status: row.status ?? newOffice.status,
        },
        ...prev,
      ]));
      setShowModal(false);
      setNewOffice({
        project_id: '',
        office_name: '',
        address: '',
        contact_person: '',
        contact_phone: '',
        contact_email: '',
        setup_date: '',
        facilities: '',
        status: 'Active',
      });
      notifySuccess('Site office added successfully.');
    } catch (error) {
      console.error('Create site office failed via API:', error);
      setFormError('Unable to add site office.');
      notifyError('Unable to add site office. Please retry.');
    }
  };

  const handleUpdateSiteOffice = async () => {
    setFormError('');
    if (!editOffice || !newOffice.project_id || !newOffice.office_name.trim() || !newOffice.address.trim()) {
      setFormError('Project, office name, and address are required.');
      return;
    }
    if (newOffice.contact_phone && !validatePhone(newOffice.contact_phone)) {
      setFormError('Phone must be 10 digits.');
      return;
    }
    if (!(await confirmDialog('Update this site office?', 'Update Site Office'))) return;
    try {
      const payload = {
        projectId: Number(newOffice.project_id),
        officeName: newOffice.office_name.trim(),
        address: newOffice.address.trim(),
        contactPerson: newOffice.contact_person.trim() || null,
        contactPhone: newOffice.contact_phone.trim() || null,
        contactEmail: newOffice.contact_email.trim() || null,
        setupDate: newOffice.setup_date ? new Date(newOffice.setup_date).toISOString() : null,
        facilities: newOffice.facilities.trim() || null,
        status: newOffice.status,
      };
      const response = await scopeAPI.updateSiteOffice(editOffice.id, payload);
      const row = response?.data;
      setSiteOffices((prev) => prev.map((o) => (o.id === editOffice.id ? {
        ...o,
        project_id: row.projectId ?? o.project_id,
        office_name: row.officeName ?? o.office_name,
        address: row.address ?? o.address,
        contact_person: row.contactPerson ?? o.contact_person,
        contact_phone: row.contactPhone ?? o.contact_phone,
        contact_email: row.contactEmail ?? o.contact_email,
        setup_date: row.setupDate ? String(row.setupDate).slice(0, 10) : o.setup_date,
        facilities: row.facilities ?? o.facilities,
        status: row.status ?? o.status,
      } : o)));
      setShowModal(false);
      setEditOffice(null);
      notifySuccess('Site office updated successfully.');
    } catch (error) {
      console.error('Update site office failed:', error);
      setFormError('Unable to update site office.');
      notifyError('Unable to update site office. Please retry.');
    }
  };

  const handleDeleteSiteOffice = async (office) => {
    if (!(await confirmDialog(`Delete "${office.office_name}"?`, 'Delete'))) return;
    try {
      await scopeAPI.deleteSiteOffice(office.id);
      setSiteOffices((prev) => prev.filter((o) => o.id !== office.id));
      notifySuccess('Site office deleted.');
    } catch (error) {
      console.error('Delete site office failed:', error);
      notifyError('Unable to delete site office. Please retry.');
    }
  };

  const filteredOffices = projectFilter === 'all'
    ? siteOffices
    : siteOffices.filter(o => o.project_id === parseInt(projectFilter, 10));

  return (
    <div>
      <div className="section-header">
        <h2>Site Office <span className="count-badge">{filteredOffices.length}</span></h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className="btn-secondary"
            onClick={() => printTableElement({ title: 'Site Office - Filtered', subtitle: `Project: ${projectFilter === 'all' ? 'All Projects' : (projects.find(p => p.id === parseInt(projectFilter, 10))?.name || 'Selected Project')}`, element: contentRef.current })}
          >
            Download PDF
          </button>
          <button className="btn-primary" onClick={openAddModal}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
            Add Site Office
          </button>
        </div>
      </div>

      <div className="filter-bar">
        <ProjectFilterSelect
          value={projectFilter}
          onChange={setProjectFilter}
          projects={projects}
        />
        <ResetFiltersButton onClick={() => setProjectFilter('all')} />
      </div>

      <div className="cards-grid" ref={contentRef}>
        {filteredOffices.map((office) => {
          const project = projects.find(p => p.id === office.project_id);
          return (
            <div key={office.id} className="card" style={{ cursor: 'default' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <div>
                  <h3 style={{ fontSize: 15, fontWeight: 600, color: '#1f2937', margin: 0 }}>{office.office_name}</h3>
                  <p style={{ fontSize: 12, color: '#6b7280', margin: '4px 0 0 0' }}>{project?.name}</p>
                </div>
                <span className={`status-badge ${office.status === 'Active' ? 'active' : 'closed'}`}>{office.status}</span>
              </div>

              <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.5, marginBottom: 12, padding: '8px 12px', background: '#f9fafb', borderRadius: 6 }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14, verticalAlign: 'middle', marginRight: 6 }}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>
                {office.address}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, fontSize: 13 }}>
                <div><span style={{ color: '#6b7280' }}>Contact:</span> <strong>{office.contact_person}</strong></div>
                <div><span style={{ color: '#6b7280' }}>Phone:</span> {office.contact_phone}</div>
                <div><span style={{ color: '#6b7280' }}>Setup:</span> {formatDateIN(office.setup_date)}</div>
              </div>

              <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #e5e7eb' }}>
                <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 4px 0' }}>Facilities</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {(office.facilities ? office.facilities.split(', ').filter(Boolean) : []).map((f, i) => (
                    <span key={i} style={{ padding: '2px 8px', background: '#F5F3FF', color: '#7C3AED', borderRadius: 10, fontSize: 11, fontWeight: 500 }}>{f}</span>
                  ))}
                  {!office.facilities && (
                    <span style={{ fontSize: 12, color: '#9ca3af' }}>-</span>
                  )}
                </div>
              </div>
              <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                <button className="btn-secondary btn-sm" onClick={(e) => { e.stopPropagation(); openEditModal(office); }}>Edit</button>
                <button className="btn-secondary btn-sm" style={{ borderColor: '#fecaca', color: '#b91c1c' }} onClick={(e) => { e.stopPropagation(); handleDeleteSiteOffice(office); }}>Delete</button>
              </div>
            </div>
          );
        })}
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>{editOffice ? 'Edit Site Office' : 'Add Site Office'}</h2>
              <button className="btn-close" onClick={() => setShowModal(false)}><svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg></button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group"><label>Project <span className="req">*</span></label><select value={newOffice.project_id} onChange={(e) => setNewOffice({ ...newOffice, project_id: e.target.value })}><option value="">Select</option>{projects.filter(p => p.status === 'Active').map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select></div>
                <div className="form-group"><label>Office Name <span className="req">*</span></label><input type="text" value={newOffice.office_name} onChange={(e) => setNewOffice({ ...newOffice, office_name: e.target.value })} placeholder="e.g. Main Site Office" /></div>
              </div>
              <div className="form-group"><label>Address <span className="req">*</span></label><textarea value={newOffice.address} onChange={(e) => setNewOffice({ ...newOffice, address: e.target.value })} placeholder="Full address..." rows={2} /></div>
              <div className="form-row">
                <div className="form-group"><label>Contact Person</label><input type="text" value={newOffice.contact_person} onChange={(e) => setNewOffice({ ...newOffice, contact_person: e.target.value })} /></div>
                <div className="form-group"><label>Phone</label><input type="tel" value={newOffice.contact_phone} onChange={(e) => setNewOffice({ ...newOffice, contact_phone: e.target.value.replace(/\D/g, '').slice(0, 10) })} placeholder="10 digits" maxLength={10} /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Email</label><input type="email" value={newOffice.contact_email} onChange={(e) => setNewOffice({ ...newOffice, contact_email: e.target.value })} placeholder="office@company.com" /></div>
                <div className="form-group"><label>Setup Date</label><input type="date" value={newOffice.setup_date} onChange={(e) => setNewOffice({ ...newOffice, setup_date: e.target.value })} /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Status</label><select value={newOffice.status} onChange={(e) => setNewOffice({ ...newOffice, status: e.target.value })}><option value="Active">Active</option><option value="Inactive">Inactive</option></select></div>
              </div>
              <div className="form-group"><label>Facilities</label><input type="text" value={newOffice.facilities} onChange={(e) => setNewOffice({ ...newOffice, facilities: e.target.value })} placeholder="Comma separated: AC, CCTV, DG Set..." /></div>
              {formError && <p style={{ color: '#dc2626', fontSize: 12 }}>{formError}</p>}
              <div className="modal-actions">
                <button className="btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                {editOffice ? (
                  <button className="btn-primary" onClick={handleUpdateSiteOffice}>Update Office</button>
                ) : (
                  <button className="btn-primary" onClick={handleCreateSiteOffice}>Add Office</button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SiteOffice;
